import { useMemo } from "react";

import { useHistory } from "react-router";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  useGetAllRelationships,
  useGetAllTransfers,
  useGetIsLegalEntity,
  useGetOwnerDetails,
} from "~/v2/api/hooks";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import {
  getLastTransferType,
  isACHPendingMicroDeposit,
  useIsACHOptionAllowed,
} from "../common/util";
import { useUnlinkRelationship } from "../LinkedAccounts/useUnlinkRelationship";

export const useWithdrawController = () => {
  const history = useHistory();
  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { isLegalEntity } = useGetIsLegalEntity();
  const { details } = useGetOwnerDetails();

  const { ach, wire, currencyCloud } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  const transfers = useGetAllTransfers({
    accountID: currentAccount?.id || "",
    isCurrencyCloudEnabled: currencyCloud.isEnabled,
  });
  const lastTransferType = useMemo(
    () => getLastTransferType(transfers, "OUTGOING"),
    [transfers],
  );

  // Find the first active (non-canceled, non-rejected) relationship for use
  const activeAchRelationship = ach.relationships.find(
    (rel) => rel.status !== "CANCELED" && rel.status !== "REJECTED",
  );

  // Also check if there's a rejected relationship that needs to be handled
  const rejectedAchRelationship = ach.relationships.find(
    (rel) => rel.status === "REJECTED",
  );

  // Use active relationship if available, otherwise show rejected for unlinking
  const achRelationship = activeAchRelationship || rejectedAchRelationship;

  const isACHMicroDepositVerificationRequired = isACHPendingMicroDeposit(
    activeAchRelationship,
  );

  const wireRelationship = wire.relationships.at(0);
  const isWireRelationshipInternational =
    !!wireRelationship &&
    wireRelationship.country !== "US" &&
    wireRelationship.country !== "USA" &&
    wireRelationship.country !== "";

  const {
    unlinkModal,
    unlinkRelationship,
    isLoading: isUnlinkLoading,
  } = useUnlinkRelationship({
    accountID: currentAccount?.id ?? "",
    onSuccess: (relationship) => {
      if (relationship?.type === "ACH") {
        ach.refetch();
      } else if (relationship?.type === "Wire") {
        wire.refetch();
      }
      toastSuccess(`${relationship.type} connection removed`);
    },
    onError: () => {
      toastError(`An error occurred while removing the bank connection.`);
    },
  });

  const onWireClicked = (isInternational?: boolean) => () => {
    if (!wireRelationship) {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_WIRE,
        search: `?transfer=withdraw${
          isInternational ? "&international=true" : ""
        }`,
      });
      return;
    }

    if (wireRelationship.status === "REJECTED") {
      unlinkRelationship({ ...wireRelationship, type: "Wire" });
      return;
    }

    history.push(Path.ROUTE_FUNDING_WITHDRAW_WIRE);
  };

  const onACHClicked = () => {
    // If no active relationship or micro deposit verification is required, go to link page
    if (!activeAchRelationship || isACHMicroDepositVerificationRequired) {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_ACH,
        search: `?transfer=withdraw${
          isACHMicroDepositVerificationRequired
            ? `&micro_deposit_verification_required=true&relationship_id=${
                activeAchRelationship?.id ?? ""
              }`
            : ""
        }`,
      });
      return;
    }

    // If there's a rejected relationship, offer to unlink it
    if (rejectedAchRelationship) {
      unlinkRelationship({ ...rejectedAchRelationship, type: "ACH" });
      return;
    }

    history.push(Path.ROUTE_FUNDING_WITHDRAW_ACH);
  };

  const onCurrencyCloudClicked = () => {
    if (!currencyCloud.account) {
      history.push({
        pathname: Path.ROUTE_FUNDING_LINK_CURRENCYCLOUD,
        search: "?transfer=withdraw",
      });
      return;
    }
    history.push(Path.ROUTE_FUNDING_WITHDRAW_CURRENCYCLOUD);
  };

  const onBackClicked = () => history.push(Path.ROUTE_FUNDING);

  const isACHOptionAllowed = useIsACHOptionAllowed({
    isLegalEntity,
    hasACHRelationship: !!achRelationship,
  });

  // IRA distributions are limited to US transfer types (ACH + domestic wire).
  const isIRA = currentAccount?.account_type === "ira";
  const isCurrencyCloudOptionAllowed = currencyCloud.isEnabled && !isIRA;

  const isLoading =
    !currentAccount ||
    !details ||
    ach.isLoading ||
    wire.isLoading ||
    currencyCloud.isLoading;

  return {
    isLoading,
    onBackClicked,
    isACHOptionAllowed,
    achRelationship,
    lastTransferType,
    isUnlinkLoading,
    onACHClicked,
    wireRelationship,
    isWireRelationshipInternational,
    onWireClicked,
    isCurrencyCloudOptionAllowed,
    currencyCloudAccount: currencyCloud.account,
    onCurrencyCloudClicked,
    unlinkModal,
  };
};
